/**
 * @description Pack chrome flag for SO-operator surfaces (LYN-356, LYN-361)
 *
 * Key features:
 * - Reads GET /api/features/pack-chrome
 * - Views consume the pack boolean — no parent-CP slug checks
 * - Last-known hint so refresh does not fail-open to LynxPay chrome
 *
 * @dependencies apiClient, AuthContext, packChromeHint
 *
 * @notes
 * - Until LYN-289 the allowlist lives in backend/utilities/packSoChrome.js
 * - CP admin and LynxPay SO stay false
 * - No second feature-flag table
 */

import React, { createContext, useContext, useEffect, useMemo, useState } from 'react';
import { useUser } from './AuthContext';
import apiClient from '../services/api/apiClient';
import { readPackChromeHint, writePackChromeHint } from '../utils/packChromeHint';

const PackChromeContext = createContext();

const PACK_CHROME_ABSENT = {
  towtechSoChrome: false,
  loading: false,
  addCompanyOpen: false,
  openAddCompany: () => {},
  closeAddCompany: () => {}
};

export const PackChromeProvider = ({ children }) => {
  const { isSignedIn, isLoaded, user } = useUser();
  const [towtechSoChrome, setTowtechSoChrome] = useState(() => readPackChromeHint(user?.id) === true);
  const [loading, setLoading] = useState(true);
  const [addCompanyOpen, setAddCompanyOpen] = useState(false);

  useEffect(() => {
    let cancelled = false;

    const load = async () => {
      if (!isLoaded) {
        return;
      }
      if (!isSignedIn || user?.role !== 'so') {
        if (!cancelled) {
          setTowtechSoChrome(false);
          setLoading(false);
        }
        return;
      }

      const hint = readPackChromeHint(user?.id);
      if (hint === true && !cancelled) {
        setTowtechSoChrome(true);
      }

      try {
        const response = await apiClient.get('/features/pack-chrome');
        if (!cancelled) {
          const enabled = !!response.data?.data?.packs?.towtechSoChrome;
          setTowtechSoChrome(enabled);
          writePackChromeHint(enabled, user?.id);
        }
      } catch (error) {
        console.error('Failed to resolve pack chrome:', error);
        if (!cancelled) {
          const fallback = readPackChromeHint(user?.id);
          setTowtechSoChrome(fallback === true);
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    load();
    return () => {
      cancelled = true;
    };
  }, [isLoaded, isSignedIn, user?.role, user?.id]);

  const openAddCompany = () => setAddCompanyOpen(true);
  const closeAddCompany = () => setAddCompanyOpen(false);

  const value = useMemo(() => ({
    towtechSoChrome,
    loading,
    addCompanyOpen,
    openAddCompany,
    closeAddCompany
  }), [towtechSoChrome, loading, addCompanyOpen]);

  return (
    <PackChromeContext.Provider value={value}>
      {children}
    </PackChromeContext.Provider>
  );
};

export const usePackChrome = () => {
  const context = useContext(PackChromeContext);
  if (context === undefined) {
    throw new Error('usePackChrome must be used within a PackChromeProvider');
  }
  return context;
};

export const usePackChromeIfPresent = () => {
  const context = useContext(PackChromeContext);
  return context === undefined ? PACK_CHROME_ABSENT : context;
};

export default PackChromeContext;
